import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Layout } from '@/components/Layout';
import { Canvas } from '@/components/Canvas/Canvas';
import { Gallery } from '@/components/Gallery/Gallery';
import { ArchiveDetail } from '@/components/Gallery/ArchiveDetail';
import { Login } from '@/components/Auth/Login';
import { Register } from '@/components/Auth/Register';
import { VerifyEmail } from '@/components/Auth/VerifyEmail';
import { ForgotPassword } from '@/components/Auth/ForgotPassword';
import { ResetPassword } from '@/components/Auth/ResetPassword';
import { Profile } from '@/components/Profile/Profile';

const UsersPanel = lazy(() =>
  import('@/components/Admin/UsersPanel').then((module) => ({ default: module.UsersPanel }))
);
const BugReportsPanel = lazy(() =>
  import('@/components/Admin/BugReportsPanel').then((module) => ({
    default: module.BugReportsPanel,
  }))
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wall-accent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Canvas />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="gallery/:id" element={<ArchiveDetail />} />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route
          path="admin-panel/users"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wall-accent"></div>
                  </div>
                }
              >
                <UsersPanel />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="admin-panel/reports"
          element={
            <ProtectedRoute>
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wall-accent"></div>
                  </div>
                }
              >
                <BugReportsPanel />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}
